import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react';
import { usePlaidLink } from 'react-plaid-link';

import Loader from '../../../components/Loader';
import SkipBankingSurvey from '../SkipBankingSurvey';

import { runPlaid } from '../../../services/integrationsService';
import { storeBankingEvents } from '../../../services/truststartService';
import { LanguageContext } from '../../../config/LanguageContext';

const safelyStoreBankingEvents = async (events, requestId, clientId) => {
  if (!events.length) return;
  try {
    const persistentEvents = [...events];
    // eslint-disable-next-line no-param-reassign
    events.length = 0;
    await storeBankingEvents({
      events: persistentEvents.sort((a, b) => new Date(a.metadata.timestamp) - new Date(b.metadata.timestamp))
        .map((event) => ({
          smartConsentSessionId: event.metadata.link_session_id,
          timestamp: event.metadata.timestamp,
          detail: event.eventName === 'SELECT_INSTITUTION'
            ? `${event.eventName}: ${event.metadata.institution_name}` : event.eventName,
          sessionDetails: {
            sessionId: event.metadata.link_session_id,
            plaidRequestId: event.metadata.request_id,
          },
          isComplete: event.eventName === 'HANDOFF',
          provider: 'plaid',
          source: 'smartconsent',
          rawEvent: event,
        })),
      requestId,
      clientId,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Unable to store banking events', error);
  }
};

async function handleOnSuccess(props, token, metadata) {
  const {
    clientId, requestId, nextStep, createScoringRequest,
  } = props;
  if (createScoringRequest) {
    await createScoringRequest();
  }
  await runPlaid(requestId, clientId, { public_token: token, metadata, resume: true });
  nextStep();
}

function PlaidLinkView(props) {
  const { language } = useContext(LanguageContext);
  const [linkToken, setLinkToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSkipButtonOpen, setIsSkipButtonOpen] = useState(false);
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const events = [];
  const {
    clientId,
    requestId,
    skipBankingSurvey,
    nextStep,
  } = props;

  useEffect(() => {
    async function loadBankingConfig() {
      setLoading(true);
      const [{ link_token: token }] = await runPlaid(requestId, clientId, { language });
      setLinkToken(token);
      setLoading(false);
    }
    loadBankingConfig();
  }, [clientId, language, requestId, shouldUpdate]);

  const onEvent = (eventName, metadata) => {
    events.push({ eventName, metadata });
    if (['OPEN', 'EXIT', 'HANDOFF', 'ERROR'].includes(eventName)) {
      safelyStoreBankingEvents(
        events,
        requestId,
        clientId,
      );
    }
  };

  const onSuccess = useCallback((publicToken, metadata) => {
    setLoading(true);
    handleOnSuccess(props, publicToken, metadata);
  }, [props]);

  const onExit = useCallback((error) => {
    if (error?.error_code === 'INVALID_LINK_TOKEN') {
      setShouldUpdate(error.error_code);
    } else setIsSkipButtonOpen(true);
  }, []);

  const handleSurveyClose = useCallback((open) => (didSkip) => {
    if (didSkip) nextStep();
    else {
      setIsSkipButtonOpen(false);
      open();
    }
  }, [nextStep]);

  const config = {
    token: linkToken,
    onEvent,
    onSuccess,
    onExit,
  };

  const {
    open,
    ready,
  } = usePlaidLink(config);

  if (loading) return <Loader />;

  if (ready && !isSkipButtonOpen) open();

  return (
    <SkipBankingSurvey
      clientId={clientId}
      requestId={requestId}
      skipBankingSurvey={skipBankingSurvey}
      isOpen={isSkipButtonOpen}
      onClose={handleSurveyClose(open)}
      language={language}
    />
  );
}

export default PlaidLinkView;
